// packages/web/src/hooks/useServicePricing.ts
// Hook for calculating service pricing based on selections

import { useMemo } from 'react';

import { MarketplaceService } from '../types/pricing';
import { findVariant } from '../utils/variantHelpers';

interface UseServicePricingResult {
  selectedVariantPrice: number | undefined;
}

export const useServicePricing = (
  service: MarketplaceService | undefined,
  selectedVariant: string | undefined,
  selectedFrequency: string | undefined,
  selectedTerm?: number | null,
): UseServicePricingResult => {
  const selectedVariantPrice = useMemo(() => {
    if (!service) return undefined;

    const getServiceLevelFallback = (): number | undefined => {
      const explicitMin = Number(service.price_min_cents ?? service.price_range_min);
      if (Number.isFinite(explicitMin) && explicitMin > 0) {
        return explicitMin;
      }

      const explicitBase = Number(service.price_cents ?? service.base_price);
      if (Number.isFinite(explicitBase) && explicitBase > 0) {
        return explicitBase;
      }

      return undefined;
    };

    if (service.pricing_model === 'variants' && service.variants && selectedVariant) {
      const variant = findVariant(service, selectedVariant);
      if (variant) {
        // Check if service has contract terms and use term-specific pricing
        let pricing = variant.pricing;
        const hasContractTerms =
          service.contract_term_months && service.contract_term_months.length > 0;

        if (hasContractTerms && selectedTerm && variant.term_overrides) {
          // Use term-specific pricing for contract services
          const termOverride = variant.term_overrides.find(
            (override: any) => override.term_months === selectedTerm,
          );
          if (termOverride) {
            pricing = termOverride;
          }
        }

        if (!pricing) return getServiceLevelFallback();
        const resolvedPrice = pricing.min_recurring_cents || pricing.recurring_cents;
        return resolvedPrice > 0 ? resolvedPrice : getServiceLevelFallback();
      }
    } else if (
      service.pricing_model === 'multi_frequency' &&
      service.frequencies &&
      selectedFrequency
    ) {
      const freq = service.frequencies.find((f) => f.frequency === selectedFrequency);
      if (freq) {
        return freq.recurring_cents;
      }
    } else if (service.pricing_model === 'varying' && service.default_pricing) {
      if (selectedVariant && service.variants) {
        const variant = findVariant(service, selectedVariant);
        if (variant) {
          let pricing = variant.pricing;
          const hasContractTerms =
            service.contract_term_months && service.contract_term_months.length > 0;
          if (hasContractTerms && selectedTerm && variant.term_overrides) {
            const termOverride = variant.term_overrides.find(
              (override: any) => override.term_months === selectedTerm,
            );
            if (termOverride) {
              pricing = termOverride;
            }
          }
          if (!pricing) return getServiceLevelFallback();
          const resolvedPrice = pricing.min_recurring_cents || pricing.recurring_cents;
          return resolvedPrice > 0 ? resolvedPrice : getServiceLevelFallback();
        }
      }
      // For varying prices without variant selection, use the minimum price for cart calculations
      if (!service.default_pricing?.min_recurring_cents) return getServiceLevelFallback();
      const fallback = service.default_pricing.min_recurring_cents;
      return fallback > 0 ? fallback : getServiceLevelFallback();
    } else if (service.default_pricing) {
      const fallback = service.default_pricing.recurring_cents;
      return fallback > 0 ? fallback : getServiceLevelFallback();
    }

    return getServiceLevelFallback();
  }, [service, selectedVariant, selectedFrequency, selectedTerm]);

  return {
    selectedVariantPrice,
  };
};
